import { useMemo } from 'react'
import type { MessageItem } from 'tripPlanner/components/Chat/MessagesList'
import type { UIComponentData } from 'tripPlanner/components/Chat/Message'
import {
  hasValidLatLng,
  MARKER_TYPE_SUGGESTED,
  MARKER_TYPE_STOP,
  MARKER_TYPE_ITEM,
  MARKER_TYPE_CHAT_HOTEL,
  MARKER_SOURCE_SUGGESTED,
  MARKER_SOURCE_ITINERARY,
} from 'tripPlanner/utils/mapMarkerTransforms'
import type { MapMarkerComponent } from 'tripPlanner/utils/mapMarkerTransforms'
import type { TripItineraryData, TripStop, TripItineraryItem, TripItemWithoutDay } from 'tripPlanner/api/types'
import type { MapPathComponent, MapRegionComponent } from 'tripPlanner/components/Chat/chatMapConstants'
import { isHotelSavedData } from 'tripPlanner/utils/savedItemDataGuards'

type MapMarker = MapMarkerComponent

// Helper to check if data is location_entities type
function isLocationEntitiesType(data: UIComponentData): boolean {
  return data.type === 'location_entities'
}

// Helper to safely get places from location_entities data
function getLocationPlaces(data: UIComponentData): Array<{
  name?: string
  lat?: number | null
  lng?: number | null
  googlePlaceId?: string | null
  formattedAddress?: string | null
  editorialSummary?: string | null
}> | undefined {
  if (data.type === 'location_entities' && 'places' in data) {
    return data.places
  }
  return undefined
}

interface MapData {
  markers: Array<MapMarker>
  paths?: Array<MapPathComponent>
  regions?: Array<MapRegionComponent>
}

type ItineraryItem = TripItineraryItem | TripItemWithoutDay

interface UseMapDataArgs {
  messages: Array<MessageItem>
  itineraryData: TripItineraryData | undefined
  selectedDayItineraryId: number | undefined
  selectedStop: TripStop | undefined
}

/**
 * Extracts map data from assistant messages.
 * Only handles location_entities - hotel markers are now managed by useSearchResults.
 * Uses the most recent assistant message that contains location_entities.
 */
function extractMapDataFromRecentMessage(messages: Array<MessageItem>): MapData | null {
  const assistantMessages = messages.filter(msg => msg.role === 'assistant')
  if (assistantMessages.length === 0) return null

  for (let i = assistantMessages.length - 1; i >= 0; i -= 1) {
    const message = assistantMessages[i]
    const locationEntitiesPart = message.parts.find(
      part => part.type === 'ui_component' && isLocationEntitiesType(part.data),
    )

    if (locationEntitiesPart?.type === 'ui_component' && isLocationEntitiesType(locationEntitiesPart.data)) {
      const places = getLocationPlaces(locationEntitiesPart.data)

      if (places && Array.isArray(places)) {
        const seenPlaceIds = new Set<string>()
        const markers = places
          .filter(place => {
            if (typeof place.lat !== 'number' || typeof place.lng !== 'number') return false
            if (place.googlePlaceId) {
              if (seenPlaceIds.has(place.googlePlaceId)) return false
              seenPlaceIds.add(place.googlePlaceId)
            }
            return true
          })
          .map(place => ({
            lat: place.lat!,
            lng: place.lng!,
            label: place.name,
            placeId: place.googlePlaceId ?? undefined,
            description: (place.editorialSummary ?? place.formattedAddress) ?? undefined,
            markerType: MARKER_TYPE_SUGGESTED,
            source: MARKER_SOURCE_SUGGESTED,
          }))

        if (markers.length === 0) continue

        return { markers }
      }
    }
  }

  return null
}

function getItemCoords(item: ItineraryItem): { lat: number; lng: number } | null {
  if (item.itemPlaceDetail && hasValidLatLng(item.itemPlaceDetail.lat, item.itemPlaceDetail.lng)) {
    return { lat: item.itemPlaceDetail.lat, lng: item.itemPlaceDetail.lng }
  }
  const offerData = isHotelSavedData(item.savedItemData) ? item.savedItemData : undefined
  const geo = offerData?.location?.geoLocation
  if (geo && hasValidLatLng(geo.lat, geo.lng)) {
    return { lat: geo.lat, lng: geo.lng }
  }
  return null
}

function isAccommodationWithOffer(item: ItineraryItem): boolean {
  return item.type === 'ACCOMMODATION' && isHotelSavedData(item.savedItemData) && Boolean(item.savedItemData.offerId)
}

function buildItemMarker(
  item: ItineraryItem,
  coords: { lat: number; lng: number },
): MapMarker {
  const isHotel = isAccommodationWithOffer(item)
  const offerId = isHotelSavedData(item.savedItemData) ? item.savedItemData.offerId : undefined
  return {
    lat: coords.lat,
    lng: coords.lng,
    label: item.name,
    description: item.description,
    placeId: item.itemPlaceDetail?.placeId,
    markerType: isHotel && offerId ? MARKER_TYPE_CHAT_HOTEL : MARKER_TYPE_ITEM,
    source: MARKER_SOURCE_ITINERARY,
    ...(isHotel && offerId ? { offerId, markerId: offerId } : {}),
  }
}

/**
 * Extracts itinerary markers (stops and items) from itinerary data.
 * Items with type ACCOMMODATION and savedItemData.offerId are emitted as chat-hotel markers
 * so they show the hotel icon and new add-to-trip hover UI.
 *
 * Also returns unresolvedHotels: accommodation items that have an offerId but no
 * coordinates. The merge logic uses these to upgrade matching location_entities
 * markers from suggested -> chat-hotel so they still get the hotel icon.
 */
function extractItineraryMarkers(itineraryData: TripItineraryData | undefined): {
  markers: Array<MapMarker>
  unresolvedHotels: Map<string, string>
} {
  const empty = { markers: [], unresolvedHotels: new Map<string, string>() }
  const stops = itineraryData?.stops ?? []
  if (stops.length === 0) return empty

  const allMarkers: Array<MapMarker> = []
  const unresolvedHotels = new Map<string, string>()
  const seenItemIds = new Set<number>()

  const processItem = (item: ItineraryItem) => {
    if (seenItemIds.has(item.id)) return
    seenItemIds.add(item.id)

    const coords = getItemCoords(item)
    if (coords) {
      allMarkers.push(buildItemMarker(item, coords))
    } else if (item.type === 'ACCOMMODATION' && isHotelSavedData(item.savedItemData) && item.savedItemData.offerId) {
      unresolvedHotels.set(item.name.toLowerCase(), item.savedItemData.offerId)
    }
  }

  stops.forEach((stop) => {
    if (
      stop.stopPlaceDetail &&
      hasValidLatLng(stop.stopPlaceDetail.lat, stop.stopPlaceDetail.lng)
    ) {
      allMarkers.push({
        lat: stop.stopPlaceDetail.lat,
        lng: stop.stopPlaceDetail.lng,
        label: stop.placeName,
        placeId: stop.stopPlaceDetail.placeId,
        description: stop.description,
        markerType: MARKER_TYPE_STOP,
        source: MARKER_SOURCE_ITINERARY,
        isOriginStop: stop.stopType === 'ORIGIN',
      })
    }

    stop.days.forEach((day) => {
      day.items.forEach((item) => processItem(item))
    })

    stop.itemsWithoutDay?.forEach((item) => processItem(item))
  })

  return { markers: allMarkers, unresolvedHotels }
}

// Stable reference for the default empty-map case so downstream memos don't see
// a new object on every render when no map data is available.
const EMPTY_MAP_DATA: MapData = { markers: [] }

/**
 * Hook that manages map data extraction and computation.
 * Extracts map markers from messages and itinerary data.
 *
 * Priority order:
 * 1. Selected day items (highest — isolated day view)
 * 2. All itinerary stops and items
 * 3. Location entities from the latest assistant message (fallback for new chats)
 *
 * Each priority level is independently memoized so the returned mapData
 * reference stays stable when a lower-priority source changes (e.g., message
 * streaming text deltas don't destabilize the itinerary-derived map data).
 *
 * Hotel markers are managed separately by useSearchResults and combined
 * in ChatTripPlannerPage.
 */
export function useMapData({
  messages,
  itineraryData,
  selectedDayItineraryId,
  selectedStop,
}: UseMapDataArgs) {
  const selectedDayItems = useMemo(() => {
    if (!selectedDayItineraryId || !selectedStop) {
      return []
    }
    const day = selectedStop.days.find((d) => d.id === selectedDayItineraryId)
    return day?.items ?? []
  }, [selectedDayItineraryId, selectedStop])

  // Priority 1: Day items when viewing a specific day itinerary (isolated view).
  // Only changes when the selected day or stop changes.
  const dayItemsMapData = useMemo((): MapData | null => {
    if (!selectedDayItineraryId || selectedDayItems.length === 0) return null
    const markers: Array<MapMarker> = []
    selectedDayItems.forEach((item) => {
      const coords = getItemCoords(item)
      if (coords) markers.push(buildItemMarker(item, coords))
    })
    return markers.length > 0 ? { markers } : null
  }, [selectedDayItineraryId, selectedDayItems])

  // Priority 2: All itinerary stops and items.
  // Only changes when itineraryData changes (enrichment, add/remove stop, etc.).
  const itineraryResult = useMemo(() => extractItineraryMarkers(itineraryData), [itineraryData])
  const itineraryMapData = useMemo((): MapData | null => {
    return itineraryResult.markers.length > 0 ? { markers: itineraryResult.markers } : null
  }, [itineraryResult])

  // Priority 3: Location entities from the latest assistant message.
  // Changes on every message update (streaming text deltas), but is only
  // used as a fallback when no itinerary or day items are available.
  const locationEntitiesMapData = useMemo(() => {
    return extractMapDataFromRecentMessage(messages)
  }, [messages])

  // Combine priorities — day items take precedence (isolated view).
  // When both itinerary and location entities exist, merge them. Deduplicates by
  // placeId and label. Hotels in the itinerary that lack coordinates are "unresolved":
  // if a matching location_entities marker exists, it's upgraded to a chat-hotel marker.
  const mapData = useMemo((): MapData => {
    if (dayItemsMapData) return dayItemsMapData

    if (itineraryMapData && locationEntitiesMapData) {
      const itineraryPlaceIds = new Set(
        itineraryMapData.markers.map(m => m.placeId).filter(Boolean),
      )
      const itineraryLabels = new Set(
        itineraryMapData.markers.map(m => m.label?.toLowerCase()).filter(Boolean),
      )
      const { unresolvedHotels } = itineraryResult

      const extraMarkers: Array<MapMarker> = []
      locationEntitiesMapData.markers.forEach(m => {
        if (m.placeId && itineraryPlaceIds.has(m.placeId)) return
        if (m.label && itineraryLabels.has(m.label.toLowerCase())) return

        const offerId = m.label ? unresolvedHotels.get(m.label.toLowerCase()) : undefined
        if (offerId) {
          extraMarkers.push({
            ...m,
            markerType: MARKER_TYPE_CHAT_HOTEL,
            source: MARKER_SOURCE_ITINERARY,
            offerId,
            markerId: offerId,
          })
        } else {
          extraMarkers.push(m)
        }
      })

      if (extraMarkers.length > 0) {
        return { markers: [...itineraryMapData.markers, ...extraMarkers] }
      }
      return itineraryMapData
    }

    if (!itineraryMapData && locationEntitiesMapData) {
      const { unresolvedHotels } = itineraryResult
      if (unresolvedHotels.size > 0) {
        const upgraded = locationEntitiesMapData.markers.map(m => {
          const offerId = m.label ? unresolvedHotels.get(m.label.toLowerCase()) : undefined
          if (offerId) {
            return {
              ...m,
              markerType: MARKER_TYPE_CHAT_HOTEL,
              source: MARKER_SOURCE_ITINERARY,
              offerId,
              markerId: offerId,
            }
          }
          return m
        })
        return { markers: upgraded }
      }
    }

    return itineraryMapData ?? locationEntitiesMapData ?? EMPTY_MAP_DATA
  }, [dayItemsMapData, itineraryMapData, locationEntitiesMapData, itineraryResult])

  const hasMapMarkers = mapData.markers && mapData.markers.length > 0

  return {
    mapData,
    hasMapMarkers,
    selectedDayItems,
  }
}
